import { useMemo, useRef } from 'react';

import { useAuth } from '@/components/Auth';
import useDataLayer, { useResultsAvailableEvent } from '@/hooks/useDataLayer';
import useDestination from '@/hooks/useDestination';
import {
  getPropertyDetails,
  getSearchDetails,
} from '@/utils/getDataLayerVariables';
import usePackageOffers from '@/v2/components/pages/PackageView/hooks/usePackageOffers';
import useProcuredOffer from '../../hooks/useProcuredOffer';
import usePropertyContent from '../../hooks/usePropertyContent';

const DataLayer = ({ pageType, path }) => {
  const propertyContent = usePropertyContent();
  const destination = useDestination();
  const procuredOffer = useProcuredOffer();
  const { memberDetails } = useAuth();

  const { searchQuery, packageOffers, availability } = usePackageOffers();

  const propertyContentRef = useRef();
  const destinationRef = useRef();

  propertyContentRef.current = propertyContent;
  destinationRef.current = destination;

  const onSearchQueryEvent = useMemo(() => {
    if (!searchQuery) {
      return null;
    }

    return {
      destinationName: destinationRef.current.title,
      event: 'pageView',
      pageType: `${pageType} With Search`,
      ...getPropertyDetails(propertyContentRef.current),
      ...getSearchDetails(searchQuery),
    };
  }, [pageType, searchQuery]);

  const onPropertyContentEvent = useMemo(
    () => ({
      destinationName: destinationRef.current.title,
      event: 'pageView',
      pageType,
      ...getPropertyDetails(propertyContent),
    }),
    [pageType, propertyContent],
  );

  const onSearchQueryAvailabilityEvent = useMemo(
    () => ({
      destinationName: destinationRef.current.title,
      pageType: `${pageType} With Search`,
      availability,
      availableRoomsCount: packageOffers?.length,
      totalRoomsCount: propertyContent.roomTypes.length,
    }),
    [availability, packageOffers, pageType, propertyContent],
  );

  const onExclusiveOfferView = useMemo(() => {
    if (!procuredOffer || !searchQuery) {
      return null;
    }

    const { travelType, country, title, searchConfig } = destination;
    const { departureDate, returnDate, adults, children, infants, originCode } =
      searchQuery;
    const { id, name } = propertyContent;

    return {
      event: 'Page Variables Ready',
      bookingChannel: 'qantas_holidays_website',
      page: path,
      pageType: 'QH Exclusive Offer Page',
      qh_user_id: memberDetails?.memberId || null,
      qffLoggedInState: !!memberDetails
        ? 'Arrived Logged In'
        : 'Arrived Logged Out',
      promotionNames: 'Exclusive Luxury Offer',
      promotionCodes: 'Exclusive Luxe Offers',
      domesticOrInternational: travelType,
      propertyName: name,
      propertyId: id,
      propertyCountryName: country,
      propertyCity: title,
      checkIn: departureDate,
      checkOut: returnDate,
      numAdults: adults,
      numChildren: children,
      numInfants: infants,
      flightFrom: originCode,
      flightTo: searchConfig.destinationCode,
    };
  }, [
    memberDetails,
    destination,
    path,
    procuredOffer,
    propertyContent,
    searchQuery,
  ]);

  useDataLayer(onExclusiveOfferView);
  useDataLayer(onSearchQueryEvent);
  useDataLayer(!searchQuery && onPropertyContentEvent);

  useResultsAvailableEvent(
    searchQuery,
    pageType === 'Package Page' ? packageOffers : null,
    onSearchQueryAvailabilityEvent,
  );

  return null;
};

export default DataLayer;
